import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { minimiseTransactions } from "@/lib/settlement";

interface PlayerTotal {
  name: string;
  net: number;
  spent: number;
  avatar_url?: string | null;
}

export const useWhoPayWho = (targetUserId?: string) => {
  return useQuery({
    queryKey: ['who-pay-who', targetUserId],
    queryFn: async () => {
      if (!targetUserId) return { playerTotals: [], transactions: [] };

      // Fetch unsettled sessions
      const { data: sessions } = await supabase
        .from('sessions')
        .select('id, buy_in_unit')
        .eq('created_by', targetUserId)
        .eq('status', 'unsettled');

      const ids = (sessions || []).map((s) => s.id);
      const sessionMap = (sessions || []).reduce((acc, s) => {
        acc[s.id] = s.buy_in_unit ?? 20;
        return acc;
      }, {} as Record<string, number>);

      // Fetch unsettled expenses
      const { data: expenses } = await supabase
        .from('expenses')
        .select(`
          id, amount, payer_id,
          payer:players!payer_id(name, avatar_url),
          participants:expense_participants(player_id, share, player:players(name, avatar_url))
        `)
        .eq('created_by', targetUserId)
        .eq('is_settled', false);

      const totals: Record<string, PlayerTotal> = {};

      if (ids.length > 0) {
        const { data: spData } = await supabase
          .from('session_players')
          .select('session_id, net_amount, buy_ins, players(name, avatar_url)')
          .in('session_id', ids);

        for (const sp of spData || []) {
          const p = (sp as any).players;
          const name = p?.name || 'Unknown';
          const avatar_url = p?.avatar_url;
          if (!totals[name]) totals[name] = { name, net: 0, spent: 0, avatar_url };
          
          // Ensure we use Number() to avoid string concatenation and round to 2 decimals
          const netAmount = Number(sp.net_amount || 0);
          totals[name].net = Math.round((totals[name].net + netAmount) * 100) / 100;
          
          const unit = sessionMap[sp.session_id] || 20;
          totals[name].spent += (sp.buy_ins || 0) * unit;
        }
      }

      for (const exp of expenses || []) {
        const p = (exp as any).payer;
        const payerName = p?.name || 'Unknown';
        const payerAvatarUrl = p?.avatar_url;
        if (!totals[payerName]) totals[payerName] = { name: payerName, net: 0, spent: 0, avatar_url: payerAvatarUrl };
        
        const expAmount = Number(exp.amount || 0);
        totals[payerName].net = Math.round((totals[payerName].net + expAmount) * 100) / 100;

        for (const part of exp.participants || []) {
          const pp = (part as any).player;
          const partName = pp?.name || 'Unknown';
          const partAvatarUrl = pp?.avatar_url;
          if (!totals[partName]) totals[partName] = { name: partName, net: 0, spent: 0, avatar_url: partAvatarUrl };
          
          const partShare = Number(part.share || 0);
          totals[partName].net = Math.round((totals[partName].net - partShare) * 100) / 100;
        }
      }

      const playerTotals = Object.values(totals).sort((a, b) => b.net - a.net);
      const transactions = minimiseTransactions(playerTotals);

      return { playerTotals, transactions };
    },
    enabled: !!targetUserId,
  });
};
